/**
 * TypedFieldSelect — Shared dropdown for field mapping with type pills.
 *
 * Used by:
 * - InputMappingGrid (Eingabe-Tab sidebar)
 * - FieldMappingGrid (Ausgabe-Tab sidebar)
 * - InputMapper (Workflow Builder bottom dock)
 *
 * Shows a button trigger with selected value + TypeChip.
 * Dropdown options are grouped and each shows name + TypeChip + optional
 * compatibility badge.
 */

import {
  Button,
  Input,
  Menu,
  MenuItem,
  type MenuProps,
  MenuSeparator,
} from '@affine/component';
import { useI18n } from '@affine/i18n';
import { ArrowDownSmallIcon, SearchIcon } from '@blocksuite/icons/rc';
import { checkCompatibility } from '@streamlain/field-types';
import type { FC, KeyboardEvent, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { DropdownChevron } from '../../shared/dropdown-chevron';
import { MappingCell } from './mapping-cell';
import { CompatDot, TypeChip } from './type-chip';
import * as styles from './typed-field-select.css';

// =============================================================================
// Types
// =============================================================================

export interface FieldOption {
  id: string;
  label: string;
  type: string;
  /** Listed, but cannot be picked (e.g. a system column). */
  disabled?: boolean;
  /** A note behind the name (e.g. what already uses the option). */
  description?: string;
}

export interface FieldOptionGroup {
  id: string;
  label: string;
  options: FieldOption[];
}

export interface TypedFieldSelectProps {
  /** Currently selected option ID (empty string = nothing selected). */
  value: string;
  /** Grouped options for the dropdown menu. */
  groups: FieldOptionGroup[];
  /** Called when the user picks an option. Empty string = cleared. */
  onChange: (optionId: string) => void;
  /** Placeholder when nothing is selected. */
  placeholder?: string;
  /** Target field type — used to compute compatibility badges. */
  targetType?: string;
  /** Optional domain-specific compatibility resolver. */
  getCompatibility?: (
    targetType: string,
    sourceType: string
  ) => 'compatible' | 'castable' | 'incompatible';
  /** Use compact sizing (for dock context). */
  compact?: boolean;
  /**
   * Two-line trigger of a mapping pair: the chosen field's name, below it
   * its type (the look of the pair's other side).
   */
  stacked?: boolean;
  /** The choice is still owed (e.g. a required input without a column). */
  invalid?: boolean;
  /** Show search input at the top of the menu (for large option lists). */
  searchable?: boolean;
  /** Search placeholder text. */
  searchPlaceholder?: string;
  /** Show group label (node name) as prefix in each option row. */
  showGroupPrefix?: boolean;
  /** Quiet, full-width selector for configuration forms. */
  appearance?: 'mapping' | 'picker';
  disabled?: boolean;
  ariaLabel?: string;
  triggerDataAttribute?: string;
  emptyLabel?: string;
  actions?: {
    label: string;
    icon?: ReactNode;
    disabled?: boolean;
    onSelect: () => void;
  }[];
}

// =============================================================================
// Menu positioning config (shared across all usages)
// =============================================================================

const menuContentOptions: MenuProps['contentOptions'] = {
  side: 'bottom' as const,
  align: 'start' as const,
  sideOffset: 4,
  style: {
    maxHeight: 'min(360px, calc(100vh - 170px))',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: '2px',
    minWidth: '240px',
    maxWidth: 'min(400px, calc(100vw - 24px))',
  },
};

/** The trigger and the option rows space their parts with a flex gap. */
const NO_CHIP_GAP = { marginLeft: 0 };
const COMPAT_GAP = { marginLeft: 2 };

// =============================================================================
// Component
// =============================================================================

export const TypedFieldSelect: FC<TypedFieldSelectProps> = ({
  value,
  groups,
  onChange,
  placeholder,
  targetType,
  getCompatibility = checkCompatibility,
  compact = false,
  stacked = false,
  invalid = false,
  searchable = false,
  searchPlaceholder,
  showGroupPrefix = false,
  appearance = 'mapping',
  disabled = false,
  ariaLabel,
  triggerDataAttribute,
  emptyLabel,
  actions = [],
}) => {
  const t = useI18n();
  const placeholderLabel =
    placeholder ?? t['com.streamlain.streamlain.common.select']();
  const searchLabel =
    searchPlaceholder ?? t['com.streamlain.streamlain.mapping.search-fields']();
  const emptyText = emptyLabel ?? t['com.streamlain.cmdk.no-results']();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // sl-n5zg.2.1: React only to real open→close / close→open transitions.
  // The effect must not fire on initial mount when the menu has never been
  // opened, so we gate the cleanup-on-close branch with a ref that flips
  // the first time the menu is actually opened.
  const hasOpenedRef = useRef(false);
  useEffect(() => {
    if (open) {
      hasOpenedRef.current = true;
      if (!searchable) return;
      const id = window.setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
    if (hasOpenedRef.current) {
      setSearch('');
    }
    return undefined;
  }, [open, searchable]);

  // sl-n5zg.2.1 (nit): memoize the selected-option lookup so we don't walk
  // the full `groups` array on every unrelated re-render.
  const selectedOption = useMemo<FieldOption | null>(() => {
    for (const group of groups) {
      const found = group.options.find(o => o.id === value);
      if (found) return found;
    }
    return null;
  }, [groups, value]);

  const selectedGroup = useMemo<FieldOptionGroup | null>(() => {
    for (const group of groups) {
      if (group.options.some(o => o.id === value)) return group;
    }
    return null;
  }, [groups, value]);

  // sl-n5zg.2.1: Extended filter — node name (group.label) also matches the
  // query, so typing a node label shows all of that node's options without
  // having to know any individual field name. Empty queries return all
  // groups unchanged.
  const filteredGroups = useMemo(() => {
    if (!search) return groups;
    const q = search.toLowerCase();
    return groups
      .map(g => {
        const groupLabelMatches = g.label.toLowerCase().includes(q);
        if (groupLabelMatches) return g;
        return {
          ...g,
          options: g.options.filter(
            o =>
              o.label.toLowerCase().includes(q) ||
              o.type.toLowerCase().includes(q)
          ),
        };
      })
      .filter(g => g.options.length > 0);
  }, [groups, search]);

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Keep menu typeahead from taking focus away while entering a query.
    if (e.key.length === 1) e.stopPropagation();
    if (e.key === 'Escape' && search.length > 0) {
      e.stopPropagation();
      setSearch('');
    }
  };

  if (appearance === 'picker') {
    return (
      <Menu
        rootOptions={{ open, onOpenChange: setOpen }}
        contentOptions={{
          align: 'start',
          sideOffset: 6,
          className: styles.pickerMenu,
        }}
        items={
          <>
            {searchable && (
              <div className={styles.searchArea}>
                <Input
                  ref={searchInputRef}
                  aria-label={searchLabel}
                  placeholder={searchLabel}
                  value={search}
                  onChange={setSearch}
                  onKeyDown={handleSearchKeyDown}
                  className={styles.searchInput}
                  preFix={<SearchIcon className={styles.searchIcon} />}
                />
              </div>
            )}
            <div className={styles.options}>
              {!search && (
                <MenuItem
                  className={styles.option}
                  selected={!value}
                  onSelect={() => onChange('')}
                >
                  <span className={styles.placeholder}>{placeholderLabel}</span>
                </MenuItem>
              )}
              {filteredGroups.map(group => (
                <div key={group.id} className={styles.optionGroup}>
                  {filteredGroups.length > 1 && (
                    <div className={styles.groupLabel}>{group.label}</div>
                  )}
                  {group.options.map(option => (
                    <MenuItem
                      key={option.id}
                      className={styles.option}
                      selected={option.id === value}
                      disabled={option.disabled}
                      onSelect={() => onChange(option.id)}
                    >
                      <span className={styles.optionText}>
                        <span>{option.label}</span>
                        {option.description && (
                          <span className={styles.description}>
                            {option.description}
                          </span>
                        )}
                      </span>
                    </MenuItem>
                  ))}
                </div>
              ))}
              {filteredGroups.length === 0 && (
                <div className={styles.empty}>{emptyText}</div>
              )}
            </div>
            {actions.length > 0 && (
              <div className={styles.actions}>
                {actions.map(action => (
                  <MenuItem
                    key={action.label}
                    className={styles.option}
                    prefixIcon={action.icon}
                    disabled={action.disabled}
                    onSelect={action.onSelect}
                  >
                    {action.label}
                  </MenuItem>
                ))}
              </div>
            )}
          </>
        }
      >
        <Button
          variant="plain"
          className={styles.trigger}
          disabled={disabled}
          aria-label={ariaLabel}
          contentClassName={selectedOption ? styles.value : styles.placeholder}
          suffix={<ArrowDownSmallIcon />}
          suffixClassName={styles.chevron}
          {...(triggerDataAttribute ? { [triggerDataAttribute]: '' } : {})}
        >
          {selectedOption?.label ?? placeholderLabel}
        </Button>
      </Menu>
    );
  }

  return (
    <Menu
      rootOptions={{ open, onOpenChange: setOpen }}
      items={
        <div>
          {searchable && (
            <div className={styles.mappingSearchArea}>
              <input
                ref={searchInputRef}
                type="text"
                className={styles.mappingSearchInput}
                aria-label={searchLabel}
                placeholder={searchLabel}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
                onKeyDown={handleSearchKeyDown}
              />
            </div>
          )}
          {/* None / clear option */}
          <MenuItem onClick={() => onChange('')}>
            <div className={styles.mappingOption}>
              <span className={styles.mappingCheck}>{!value ? '✓' : ''}</span>
              <span className={styles.mappingMuted}>{placeholderLabel}</span>
            </div>
          </MenuItem>
          {/* sl-n5zg.2.1: empty-state when the query filters everything out */}
          {searchable && search.length > 0 && filteredGroups.length === 0 && (
            <div className={styles.mappingEmpty}>{emptyText}</div>
          )}
          {filteredGroups.map(group => (
            <div key={group.id}>
              <MenuSeparator />
              {filteredGroups.length > 1 && (
                <MenuItem disabled>
                  <span className={styles.mappingGroupLabel}>
                    {group.label}
                  </span>
                </MenuItem>
              )}
              {group.options.map(opt => {
                const compat = targetType
                  ? getCompatibility(targetType, opt.type)
                  : 'compatible';
                return (
                  <MenuItem
                    key={opt.id}
                    disabled={opt.disabled}
                    onClick={() => onChange(opt.id)}
                  >
                    <div className={styles.mappingOption}>
                      <span className={styles.mappingCheck}>
                        {opt.id === value ? '✓' : ''}
                      </span>
                      <span className={styles.value}>
                        {showGroupPrefix && (
                          <span className={styles.mappingPrefix}>
                            {group.label}
                            {' › '}
                          </span>
                        )}
                        {opt.label}
                        {opt.description && (
                          <span className={styles.mappingPrefix}>
                            {' · '}
                            {opt.description}
                          </span>
                        )}
                      </span>
                      <TypeChip type={opt.type} small={compact} />
                      {/* Compat indicator — always shown */}
                      <CompatDot compatibility={compat} style={COMPAT_GAP} />
                    </div>
                  </MenuItem>
                );
              })}
            </div>
          ))}
        </div>
      }
      contentOptions={menuContentOptions}
    >
      <button
        type="button"
        className={
          stacked
            ? styles.mappingTriggerStacked
            : compact
              ? styles.mappingTriggerCompact
              : styles.mappingTrigger
        }
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        data-invalid={invalid || undefined}
      >
        {stacked ? (
          <MappingCell
            name={selectedOption?.label ?? placeholderLabel}
            type={selectedOption?.type}
            prefix={
              showGroupPrefix && selectedOption
                ? selectedGroup?.label
                : undefined
            }
            muted={!selectedOption}
          />
        ) : selectedOption ? (
          <>
            <span className={styles.value}>
              {showGroupPrefix && selectedGroup && (
                <span className={styles.mappingMuted}>
                  {selectedGroup.label}
                  {' › '}
                </span>
              )}
              {selectedOption.label}
            </span>
            {/* In the dock the input names its type above the select; the chip only shows a type that differs. */}
            {compact && selectedOption.type === targetType ? null : (
              <TypeChip
                type={selectedOption.type}
                small={compact}
                style={NO_CHIP_GAP}
              />
            )}
          </>
        ) : (
          <span className={styles.placeholder}>{placeholderLabel}</span>
        )}
        <DropdownChevron />
      </button>
    </Menu>
  );
};
